/*
This code is copied from https://github.com/sounderfm/react-circular-slider fork of the https://github.com/mnkhouri/react-circular-slider repository,
which is a Github repository for react-circular-slider-svg NPM package (MIT license).
*/

import * as React from 'react';
import {
  angleToPosition,
  positionToAngle,
  AngleDescription,
  valueToAngle,
  angleToValue,
} from './circularGeometry';
import { arcPathWithRoundedEnds } from './svgPaths';

type Props = {
  size: number;
  minValue: number;
  maxValue: number;
  startAngle: number; // 0 - 360 degrees
  endAngle: number; // 0 - 360 degrees
  angleType: AngleDescription;
  handleSize: number;
  handle: {
    value: number;
    onChange?: (value: number) => void;
    color?: string;
  };
  onControlFinished?: () => void;
  disabled?: boolean;
  arcColor: string;
  arcBackgroundColor: string;
  coerceToInt?: boolean;
  outerShadow?: boolean;
  trackWidth: number;
};

export class CircularSlider extends React.Component<Props> {
  static defaultProps: Pick<
    Props,
    | 'size'
    | 'minValue'
    | 'maxValue'
    | 'startAngle'
    | 'endAngle'
    | 'angleType'
    | 'arcBackgroundColor'
    | 'handleSize'
    | 'trackWidth'
  > = {
    size: 200,
    minValue: 0,
    maxValue: 100,
    startAngle: 0,
    endAngle: 360,
    angleType: {
      direction: 'cw',
      axis: '-y',
    },
    handleSize: 8,
    arcBackgroundColor: '#aaa',
    trackWidth: 4,
  };
  svgRef = React.createRef<SVGSVGElement>();

  onMouseEnter = (ev: React.MouseEvent<SVGSVGElement>) => {
    if (ev.buttons === 1) {
      // The left mouse button is pressed, act as though user clicked us
      this.onMouseDown(ev);
    }
  };

  onMouseDown = (ev: React.MouseEvent<SVGSVGElement>) => {
    const svgRef = this.svgRef.current;
    if (svgRef) {
      svgRef.addEventListener('mousemove', this.processSelection);
      svgRef.addEventListener('mouseleave', this.removeMouseListeners);
      svgRef.addEventListener('mouseup', this.removeMouseListeners);
    }
    this.processSelection(ev);
  };

  removeMouseListeners = () => {
    const svgRef = this.svgRef.current;
    if (svgRef) {
      svgRef.removeEventListener('mousemove', this.processSelection);
      svgRef.removeEventListener('mouseleave', this.removeMouseListeners);
      svgRef.removeEventListener('mouseup', this.removeMouseListeners);
    }
    if (this.props.onControlFinished) {
      this.props.onControlFinished();
    }
  };

  onTouchStart = (ev: React.TouchEvent<SVGSVGElement>) => {
    const svgRef = this.svgRef.current;
    if (svgRef) {
      svgRef.addEventListener('touchmove', this.processSelectionTouch);
      svgRef.addEventListener('touchend', this.removeTouchListeners);
    }
    this.processSelectionTouch(ev);
  };

  removeTouchListeners = () => {
    const svgRef = this.svgRef.current;
    if (svgRef) {
      svgRef.removeEventListener('touchmove', this.processSelectionTouch);
      svgRef.removeEventListener('touchend', this.removeTouchListeners);
    }
    if (this.props.onControlFinished) {
      this.props.onControlFinished();
    }
  };

  processSelection = (ev: React.MouseEvent<SVGSVGElement> | MouseEvent) => {
    const {
      size,
      maxValue,
      minValue,
      angleType,
      startAngle,
      endAngle,
      handle,
      disabled,
      coerceToInt,
    } = this.props;
    if (!handle.onChange) {
      // Read-only, don't bother doing calculations
      return;
    }
    const svgRef = this.svgRef.current;
    if (!svgRef) {
      return;
    }
    // Find the coordinates with respect to the SVG
    const svgPoint = svgRef.createSVGPoint();
    const x = ev.clientX;
    const y = ev.clientY;
    svgPoint.x = x;
    svgPoint.y = y;
    const coordsInSvg = svgPoint.matrixTransform(svgRef.getScreenCTM()!.inverse());

    const angle = positionToAngle(coordsInSvg, size, angleType);
    let value = angleToValue({
      angle,
      minValue,
      maxValue,
      startAngle,
      endAngle,
    });
    if (coerceToInt) {
      value = Math.round(value);
    }

    if (!disabled) {
      handle.onChange(value);
    }
  };

  processSelectionTouch = (ev: React.TouchEvent<SVGSVGElement> | TouchEvent) => {
    const {
      size,
      maxValue,
      minValue,
      angleType,
      startAngle,
      endAngle,
      handle,
      disabled,
      coerceToInt,
    } = this.props;
    if (!handle.onChange) {
      // Read-only, don't bother doing calculations
      return;
    }
    const svgRef = this.svgRef.current;
    if (!svgRef) {
      return;
    }
    // Find the coordinates with respect to the SVG
    const svgPoint = svgRef.createSVGPoint();
    const x = ev.touches[0].clientX;
    const y = ev.touches[0].clientY;
    svgPoint.x = x;
    svgPoint.y = y;
    const coordsInSvg = svgPoint.matrixTransform(svgRef.getScreenCTM()!.inverse());

    const angle = positionToAngle(coordsInSvg, size, angleType);
    let value = angleToValue({
      angle,
      minValue,
      maxValue,
      startAngle,
      endAngle,
    });
    if (coerceToInt) {
      value = Math.round(value);
    }

    if (!disabled) {
      handle.onChange(value);
    }
  };

  render() {
    const {
      size,
      handle,
      handleSize,
      maxValue,
      minValue,
      startAngle,
      endAngle,
      angleType,
      disabled,
      arcColor,
      arcBackgroundColor,
      outerShadow,
      trackWidth,
    } = this.props;
    const shadowWidth = 20;
    const trackInnerRadius = size / 2 - trackWidth - shadowWidth;
    const handleAngle = valueToAngle({
      value: handle.value,
      minValue,
      maxValue,
      startAngle,
      endAngle,
    });
    const handlePosition = angleToPosition(
      { degree: handleAngle, ...angleType },
      trackInnerRadius + trackWidth / 2,
      size,
    );

    const controllable = !disabled && Boolean(handle.onChange);

    return (
      <svg
        style={{
          touchAction: 'none', // this disables scroll while touching
          WebkitTouchCallout: 'none', // this disables long press actions
          WebkitUserSelect: 'none', // this disables long press actions
        }}
        width={size}
        height={size}
        ref={this.svgRef}
        onMouseDown={this.onMouseDown}
        onMouseEnter={this.onMouseEnter}
        onTouchStart={this.onTouchStart}
        onClick={
          /* TODO: be smarter about this -- for example, we could run this through our
          calculation and determine how close we are to the arc, and use that to decide
          if we propagate the click. */
          (ev) => controllable && ev.stopPropagation()
        }
      >
        {
          /* Shadow */
          outerShadow && (
            <React.Fragment>
              <radialGradient id="outerShadow">
                <stop offset="90%" stopColor={arcColor} />
                <stop offset="100%" stopColor="white" />
              </radialGradient>

              <circle
                fill="none"
                stroke="url(#outerShadow)"
                cx={size / 2}
                cy={size / 2}
                // Subtract an extra pixel to ensure there's never any gap between slider and shadow
                r={trackInnerRadius + trackWidth + shadowWidth / 2 - 1}
                strokeWidth={shadowWidth}
              />
            </React.Fragment>
          )
        }

        <React.Fragment>
          <linearGradient id="gradientFill" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#49c7ff" />
            <stop offset="100%" stopColor={arcColor} />
          </linearGradient>
          {/* Arc Background  */}
          <path
            d={arcPathWithRoundedEnds({
              startAngle: handleAngle,
              endAngle,
              angleType,
              innerRadius: trackInnerRadius,
              thickness: trackWidth,
              svgSize: size,
              direction: angleType.direction,
            })}
            fill={arcBackgroundColor}
          />
          {/* Arc (render after background so it overlays it) */}
          <path
            d={arcPathWithRoundedEnds({
              startAngle,
              endAngle: handleAngle,
              angleType,
              innerRadius: trackInnerRadius,
              thickness: trackWidth,
              svgSize: size,
              direction: angleType.direction,
            })}
            fill="url(#gradientFill)"
          />
        </React.Fragment>
        {
          /* Handle */
          controllable && (
            <React.Fragment>
              <filter id="handleShadow" x="-50%" y="-50%" width="16" height="16">
                <feOffset result="offOut" in="SourceGraphic" dx="0" dy="0" />
                <feColorMatrix
                  result="matrixOut"
                  in="offOut"
                  type="matrix"
                  values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0"
                />
                <feGaussianBlur result="blurOut" in="matrixOut" stdDeviation="5" />
                <feBlend in="SourceGraphic" in2="blurOut" mode="normal" />
              </filter>
              <circle
                r={handleSize}
                cx={handlePosition.x}
                cy={handlePosition.y}
                fill={handle.color ?? '"#ffffff"'}
                filter="url(#handleShadow)"
              />
            </React.Fragment>
          )
        }
      </svg>
    );
  }
}
